import queryString from "query-string";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { ExternalLink } from "./ExternalLink";
import { ResponsiveLayout } from "./ResponsiveLayout";
import analytics from "./services/analytics";
import { SizedBox } from "./SizedBox";
import { language, translation } from "./translations";
import { Paragraph1 } from "./typography";

export const AndroidStoreButton: React.FC<{
  utmContentOverride?: string;
  listing?: string;
}> = (props) => {
  const location = useLocation();
  const queryStringParams = queryString.parse(location.search);
  const utmSource = (queryStringParams.utm_source as string) ?? "homepage";
  const utmMedium = (queryStringParams.utm_medium as string) ?? "banner";
  const utmCampaign = queryStringParams.utm_campaign as string;
  const utmTerm = queryStringParams.utm_term as string;
  const utmContent =
    props.utmContentOverride ?? (queryStringParams.utm_content as string);

  const playStoreReferrerData = {
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_content: utmContent,
  };

  const playStoreReferrerDataFiltered = Object.fromEntries(
    Object.entries(playStoreReferrerData).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, value]) => value !== undefined,
    ),
  );

  const playStoreReferrer = queryString.stringify(
    playStoreReferrerDataFiltered,
  );

  const playStoreUrl = queryString.stringifyUrl({
    url: "https://play.google.com/store/apps/details",
    query: {
      id: "life.lab4.lifepal_app",
      referrer: playStoreReferrer,
      listing: props.listing,
    },
  });

  return (
    <StoreButton
      href={playStoreUrl}
      onClick={() => {
        analytics.logOpenAndroidStore();
      }}
    >
      <img
        src={
          language === "pl"
            ? "/badge-google-play-pl.svg"
            : "/badge-google-play-en.svg"
        }
        alt="Google Play"
      />
    </StoreButton>
  );
};

export const AppStoreButton: React.FC<{
  ppid?: string;
}> = (props) => {
  const location = useLocation();
  const queryStringParams = queryString.parse(location.search);
  const utmSource = (queryStringParams.utm_source as string) ?? "homepage";
  const utmCampaign = queryStringParams.utm_campaign as string;

  const appStoreUrl = queryString.stringifyUrl({
    url: "https://apps.apple.com/pl/app/vika-zyskaj-zdrowie-za-darmo/id1457836471",
    query: {
      pt: "120009564",
      ct: utmCampaign ?? utmSource,
      mt: "8",
      ppid: props.ppid,
    },
  });

  return (
    <StoreButton
      href={appStoreUrl}
      onClick={() => {
        analytics.logOpenIOSStore();
      }}
    >
      <img
        src={
          language === "pl"
            ? "/badge-app-store-pl.svg"
            : "/badge-app-store-en.svg"
        }
        alt="App Store"
      />
    </StoreButton>
  );
};

export const StoreButtonsBlock: React.FC<{
  showStoreButtonsCallToAction: boolean;
  forceVerticalLayout?: boolean;
  utmContentOverride?: string;
}> = (props) => {
  const forceVerticalLayout = props.forceVerticalLayout ?? false;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {props.showStoreButtonsCallToAction && (
        <>
          <Paragraph1 color="white">{translation.useFreeApp}</Paragraph1>

          <SizedBox desktopHeight={12} mobileHeight={16} />
        </>
      )}

      <ResponsiveLayout
        alignItems="center"
        flexDirection={forceVerticalLayout ? "column" : "row"}
      >
        <AndroidStoreButton utmContentOverride={props.utmContentOverride} />

        {forceVerticalLayout ? (
          <SizedBox height={8} />
        ) : (
          <SizedBox desktopWidth={16} mobileWidth={8} mobileHeight={8} />
        )}

        <AppStoreButton />
      </ResponsiveLayout>
    </div>
  );
};

const StoreButton = styled(ExternalLink)`
  height: 48px;
`;
